import type { GameClient } from "../net/GameClient";
import type { FirstPersonController } from "../player/FirstPersonController";

/**
 * TouchControls — on-screen input for touch devices (iPad / phones).
 *
 * iOS/iPadOS Safari has no Pointer Lock API (even with a mouse or Magic
 * Keyboard trackpad paired), so the desktop mouse-look flow can't work
 * there. This layer replaces it:
 *
 *   - LEFT ~42% of the screen: dynamic virtual joystick → the same 8-way
 *     WASD booleans the keyboard sets (wire protocol unchanged).
 *   - Any other touch on the canvas: drag to look — feeds the exact
 *     yaw/pitch pipeline pointer-locked mousemove uses (applyLookDelta).
 *   - On-screen buttons: FIRE (hold = auto fire / grenade charge, release
 *     throws), JUMP, R (reload), ZOOM (RMB: sniper scope / knife stab),
 *     NADE (slot-4 grenade cycle) and BUY (visible in buy/warmup only).
 *
 * A hardware keyboard keeps working alongside (iPad + Magic Keyboard:
 * WASD to move, touch the screen to aim, trackpad-click the canvas to
 * shoot). Instantiated only when `isTouchMode()` — desktop is untouched.
 */
export class TouchControls {
  /** Touches starting left of this fraction of the viewport = joystick. */
  private static readonly JOY_ZONE_FRACTION = 0.42;
  /** Knob travel radius (px). */
  private static readonly JOY_RADIUS = 52;
  /** Drag distance (px) before movement registers. */
  private static readonly JOY_DEAD = 10;
  /** Normalized per-axis trigger — 0.4 gives clean 8-way sectors. */
  private static readonly AXIS_THRESHOLD = 0.4;
  /** Touch look boost over raw mouse pixels — a full-screen swipe should
   *  cover ~a half turn, matching mobile-FPS conventions. The player's
   *  sensitivity setting still multiplies on top (applyLookDelta). */
  private static readonly LOOK_MULT = 2.0;

  private readonly canvas: HTMLCanvasElement;
  private readonly fpc: FirstPersonController;

  private readonly root: HTMLDivElement;
  private readonly joyBase: HTMLDivElement;
  private readonly joyKnob: HTMLDivElement;
  private buyBtn: HTMLButtonElement | null = null;

  /** Active joystick touch identifier (null = joystick idle). */
  private joyId: number | null = null;
  private joyOriginX = 0;
  private joyOriginY = 0;
  /** Active look-drag touch identifier (null = not looking). */
  private lookId: number | null = null;
  private lookLastX = 0;
  private lookLastY = 0;

  constructor(canvas: HTMLCanvasElement, fpc: FirstPersonController, client: GameClient | null) {
    this.canvas = canvas;
    this.fpc = fpc;

    this.injectStyles();
    this.root = document.createElement("div");
    this.root.id = "touch-controls";
    document.body.appendChild(this.root);

    // Joystick visuals — hidden until a left-zone touch lands; the base
    // appears under the finger (dynamic origin, mobile-FPS standard).
    this.joyBase = document.createElement("div");
    this.joyBase.className = "tc-joy-base";
    this.joyKnob = document.createElement("div");
    this.joyKnob.className = "tc-joy-knob";
    this.joyBase.appendChild(this.joyKnob);
    this.root.appendChild(this.joyBase);

    this.buildButtons(client);
    this.attachCanvasTouch();
    this.suppressBrowserGestures();
  }

  // ── Canvas touches: joystick (left zone) + look drag (everywhere else) ──

  private attachCanvasTouch(): void {
    // touchstart binds to the canvas only — touches on the buttons / HUD
    // target those elements and never reach here. move/end ride the same
    // touch (touch events keep targeting their start element), routed by
    // identifier so joystick + look + buttons all work simultaneously.
    this.canvas.addEventListener(
      "touchstart",
      (e) => {
        // Kill iOS long-press callout / double-tap zoom / synthetic mouse
        // events for canvas touches. (pointerdown already fired — the
        // fullscreen-on-gesture and audio-unlock hooks still work.)
        e.preventDefault();
        for (const t of Array.from(e.changedTouches)) {
          const joyZone = t.clientX < window.innerWidth * TouchControls.JOY_ZONE_FRACTION;
          if (joyZone && this.joyId === null) {
            this.joyId = t.identifier;
            this.joyOriginX = t.clientX;
            this.joyOriginY = t.clientY;
            this.joyBase.style.left = `${t.clientX}px`;
            this.joyBase.style.top = `${t.clientY}px`;
            this.joyBase.classList.add("active");
            this.moveKnob(0, 0);
          } else if (this.lookId === null) {
            this.lookId = t.identifier;
            this.lookLastX = t.clientX;
            this.lookLastY = t.clientY;
          }
        }
      },
      { passive: false },
    );

    this.canvas.addEventListener(
      "touchmove",
      (e) => {
        e.preventDefault(); // no scroll / rubber-band while playing
        for (const t of Array.from(e.changedTouches)) {
          if (t.identifier === this.joyId) {
            this.updateJoystick(t.clientX, t.clientY);
          } else if (t.identifier === this.lookId) {
            this.fpc.applyLookDelta(
              (t.clientX - this.lookLastX) * TouchControls.LOOK_MULT,
              (t.clientY - this.lookLastY) * TouchControls.LOOK_MULT,
            );
            this.lookLastX = t.clientX;
            this.lookLastY = t.clientY;
          }
        }
      },
      { passive: false },
    );

    const onEnd = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) {
        if (t.identifier === this.joyId) this.releaseJoystick();
        else if (t.identifier === this.lookId) this.lookId = null;
      }
    };
    this.canvas.addEventListener("touchend", onEnd);
    this.canvas.addEventListener("touchcancel", onEnd);
  }

  private updateJoystick(x: number, y: number): void {
    const { JOY_RADIUS, JOY_DEAD, AXIS_THRESHOLD } = TouchControls;
    const dx = x - this.joyOriginX;
    const dy = y - this.joyOriginY;
    const dist = Math.hypot(dx, dy);

    // Only the knob VISUAL clamps to the ring. Direction must come from
    // the raw vector — clamping dx/dy and then dividing by the unclamped
    // distance shrank the normalized components as the finger travelled
    // past the ring, so pushing the stick hard (any real thumb drag)
    // dropped below AXIS_THRESHOLD and the player stopped dead.
    const knobScale = dist > JOY_RADIUS ? JOY_RADIUS / dist : 1;
    this.moveKnob(dx * knobScale, dy * knobScale);

    if (dist < JOY_DEAD) {
      this.fpc.setTouchMove(false, false, false, false);
      return;
    }
    const nx = dx / dist;
    const ny = dy / dist;
    this.fpc.setTouchMove(
      ny < -AXIS_THRESHOLD, // forward (drag up)
      ny > AXIS_THRESHOLD,  // back
      nx < -AXIS_THRESHOLD, // left
      nx > AXIS_THRESHOLD,  // right
    );
  }

  private releaseJoystick(): void {
    this.joyId = null;
    this.joyBase.classList.remove("active");
    this.fpc.setTouchMove(false, false, false, false);
  }

  private moveKnob(dx: number, dy: number): void {
    this.joyKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;
  }

  // ── Buttons ───────────────────────────────────────────────────────────

  private buildButtons(client: GameClient | null): void {
    const hold = (
      cls: string,
      label: string,
      onDown: () => void,
      onUp?: () => void,
    ): HTMLButtonElement => {
      const btn = document.createElement("button");
      btn.className = `tc-btn ${cls}`;
      btn.textContent = label;
      btn.addEventListener(
        "touchstart",
        (e) => {
          // Keep button touches out of the canvas routing + suppress the
          // synthetic mouse events a tap would otherwise emit.
          e.preventDefault();
          e.stopPropagation();
          btn.classList.add("pressed");
          onDown();
        },
        { passive: false },
      );
      const end = (e: TouchEvent) => {
        e.preventDefault();
        btn.classList.remove("pressed");
        onUp?.();
      };
      btn.addEventListener("touchend", end);
      btn.addEventListener("touchcancel", end);
      this.root.appendChild(btn);
      return btn;
    };

    hold("tc-fire", "FIRE",
      () => this.fpc.setTouchFire(true),
      () => this.fpc.setTouchFire(false));
    hold("tc-jump", "JUMP",
      () => this.fpc.setTouchJump(true),
      () => this.fpc.setTouchJump(false));
    hold("tc-reload", "R", () => this.fpc.touchReload());
    hold("tc-zoom", "ZOOM", () => this.fpc.touchAltAction());
    hold("tc-nade", "NADE", () => this.fpc.equipSlot(4));

    // BUY — rides the existing BuyMenu `B` handler via a synthetic key
    // event; shown only while buying is actually possible (buy/warmup).
    this.buyBtn = hold("tc-buy", "BUY", () => {
      window.dispatchEvent(new KeyboardEvent("keydown", { code: "KeyB" }));
    });
    this.buyBtn.classList.add("hidden");
    client?.onMatchHud((h) => {
      const canBuy = h.phase === "buy" || h.phase === "warmup";
      this.buyBtn?.classList.toggle("hidden", !canBuy);
    });
  }

  // ── Browser gesture suppression (pinch zoom, etc.) ────────────────────

  private suppressBrowserGestures(): void {
    // iOS Safari pinch-zoom ignores touch-action — block it directly.
    document.addEventListener(
      "gesturestart",
      (e) => e.preventDefault(),
      { passive: false },
    );
  }

  private injectStyles(): void {
    if (document.getElementById("touch-controls-styles")) return;
    const style = document.createElement("style");
    style.id = "touch-controls-styles";
    style.textContent = `
      body.touch-mode {
        touch-action: none;
        -webkit-user-select: none;
        user-select: none;
        -webkit-touch-callout: none;
      }
      /* touch-action doesn't inherit — the canvas (every gameplay touch's
         target) needs its own, or Safari can claim fast swipes for
         scrolling before our preventDefault runs. */
      body.touch-mode #game-canvas {
        cursor: default;
        touch-action: none;
      }
      #touch-controls .tc-btn { touch-action: none; }

      #touch-controls {
        position: fixed;
        inset: 0;
        z-index: 9; /* above HUD (8), below buy menu (20) */
        pointer-events: none;
        font-family: 'Geist', ui-sans-serif, system-ui, sans-serif;
      }

      /* ── Joystick ── */
      #touch-controls .tc-joy-base {
        position: fixed;
        width: 128px;
        height: 128px;
        border-radius: 50%;
        border: 1.5px solid rgba(245, 239, 230, 0.35);
        background: rgba(0, 0, 0, 0.22);
        transform: translate(-50%, -50%);
        display: none;
      }
      #touch-controls .tc-joy-base.active { display: block; }
      #touch-controls .tc-joy-knob {
        position: absolute;
        left: 50%;
        top: 50%;
        width: 54px;
        height: 54px;
        border-radius: 50%;
        background: rgba(245, 239, 230, 0.55);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45);
        transform: translate(-50%, -50%);
      }

      /* ── Buttons ── */
      #touch-controls .tc-btn {
        position: fixed;
        pointer-events: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        border: 1.5px solid rgba(245, 239, 230, 0.45);
        background: rgba(0, 0, 0, 0.35);
        color: rgba(245, 239, 230, 0.9);
        font-weight: 800;
        letter-spacing: 0.06em;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
        -webkit-tap-highlight-color: transparent;
      }
      #touch-controls .tc-btn.pressed {
        background: rgba(255, 107, 0, 0.45);
        border-color: #ff6b00;
      }
      #touch-controls .tc-btn.hidden { display: none; }

      #touch-controls .tc-fire {
        width: 92px;
        height: 92px;
        right: calc(34px + env(safe-area-inset-right));
        bottom: calc(150px + env(safe-area-inset-bottom));
        font-size: 15px;
        border-color: rgba(255, 107, 0, 0.65);
      }
      #touch-controls .tc-jump {
        width: 64px;
        height: 64px;
        right: calc(148px + env(safe-area-inset-right));
        bottom: calc(96px + env(safe-area-inset-bottom));
        font-size: 12px;
      }
      #touch-controls .tc-reload {
        width: 56px;
        height: 56px;
        right: calc(48px + env(safe-area-inset-right));
        bottom: calc(262px + env(safe-area-inset-bottom));
        font-size: 15px;
      }
      #touch-controls .tc-zoom {
        width: 56px;
        height: 56px;
        right: calc(150px + env(safe-area-inset-right));
        bottom: calc(196px + env(safe-area-inset-bottom));
        font-size: 11px;
      }
      #touch-controls .tc-nade {
        width: 56px;
        height: 56px;
        right: calc(236px + env(safe-area-inset-right));
        bottom: calc(72px + env(safe-area-inset-bottom));
        font-size: 11px;
      }
      #touch-controls .tc-buy {
        width: 76px;
        height: 40px;
        border-radius: 10px;
        left: calc(20px + env(safe-area-inset-left));
        top: calc(64px + env(safe-area-inset-top));
        font-size: 13px;
        border-color: rgba(125, 255, 179, 0.6);
        color: #7dffb3;
      }
    `;
    document.head.appendChild(style);
  }
}
